// src/containers/BookingRequest/components/BookingInfoSummary.tsx
import React from "react";
import { currencySymbol } from "shared/CurrencySymbol";
import moment from "moment";
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CalendarIcon,
  HomeIcon,
  ClockIcon,
  BuildingOfficeIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

interface BookingInfoSummaryProps {
  invoiceId?: string | number | null;
  totalAmount?: number | string;
  numberOfGuests?: number;
  checkInDate?: Date;
  checkOutDate?: Date;
  numberOfMonths?: number | string;
  isSharedProperty?: boolean;
  bookedRoom?: string;
  propertyTitle?: string;
  bookingStatus?: string;
}

const BookingInfoSummary: React.FC<BookingInfoSummaryProps> = ({
  invoiceId,
  totalAmount,
  numberOfGuests,
  checkInDate,
  checkOutDate,
  numberOfMonths,
  isSharedProperty,
  bookedRoom,
  propertyTitle,
  bookingStatus,
}) => {
  const isCanceled = bookingStatus === "canceled";

  // If canceled, show a different layout
  if (isCanceled) {
    return (
      <div className="space-y-4 mb-6">
        {/* Canceled Status Banner */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/10 rounded-xl p-5 border-2 border-red-300 dark:border-red-700">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
              <XCircleIcon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900 dark:text-red-200">
                Booking Canceled
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                This booking request was canceled and is no longer active.
              </p>
            </div>
          </div>
        </div>

        {/* Property/Room Info - Muted */}
        <div className="bg-gray-100 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-300 dark:border-gray-700 opacity-75">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-400 dark:bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0">
              {isSharedProperty ? (
                <BuildingOfficeIcon className="w-5 h-5 text-white" />
              ) : (
                <HomeIcon className="w-5 h-5 text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                {isSharedProperty
                  ? "Shared Property - Room"
                  : "Entire Property"}
              </span>
              <p className="text-base font-bold text-gray-700 dark:text-gray-300 mt-1 break-words line-through">
                {isSharedProperty
                  ? bookedRoom || "Room Not Specified"
                  : propertyTitle || "Property Booking"}
              </p>
            </div>
          </div>
        </div>

        {/* Canceled Booking Details - Compact Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Period */}
          <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-300 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Period
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 line-through">
              {checkInDate && checkOutDate ? (
                <>
                  {moment(checkInDate).format("MMM DD")} -{" "}
                  {moment(checkOutDate).format("MMM DD, YYYY")}
                </>
              ) : (
                "Not specified"
              )}
            </p>
          </div>

          {/* Duration */}
          <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-300 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <ClockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Duration
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 line-through">
              {numberOfMonths || 0}{" "}
              {numberOfMonths === 1 || numberOfMonths === "1"
                ? "month"
                : "months"}
            </p>
          </div>

          {/* Guests */}
          <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-300 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <UserGroupIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Guests
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 line-through">
              {numberOfGuests || 0} {numberOfGuests === 1 ? "Guest" : "Guests"}
            </p>
          </div>

          {/* Amount */}
          <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-300 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <CurrencyDollarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Amount
              </span>
            </div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 line-through">
              {currencySymbol()}
              {Number(totalAmount)?.toFixed(2) || "0.00"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Active booking display (original design)
  return (
    <div className="space-y-4 mb-6">
      {/* Property/Room Type Card */}
      <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/10 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
            {isSharedProperty ? (
              <BuildingOfficeIcon className="w-5 h-5 text-white" />
            ) : (
              <HomeIcon className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase tracking-wide">
              {isSharedProperty ? "Shared Property - Room" : "Entire Property"}
            </span>
            <p className="text-base font-bold text-orange-900 dark:text-orange-200 mt-1 break-words">
              {isSharedProperty
                ? bookedRoom || "Room Not Specified"
                : propertyTitle || "Property Booking"}
            </p>
          </div>
        </div>
      </div>

      {/* Booking Period Card */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/10 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <CalendarIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">
              Booking Period
            </span>
            <div className="mt-1 space-y-1">
              <p className="text-sm font-bold text-blue-900 dark:text-blue-200">
                {checkInDate && checkOutDate ? (
                  <>
                    {moment(checkInDate).format("MMM DD, YYYY")}
                    <span className="mx-2 text-blue-600 dark:text-blue-400">
                      →
                    </span>
                    {moment(checkOutDate).format("MMM DD, YYYY")}
                  </>
                ) : (
                  "Dates not specified"
                )}
              </p>
              {numberOfMonths && (
                <div className="flex items-center gap-2 mt-2">
                  <ClockIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <span className="font-semibold">{numberOfMonths}</span>{" "}
                    {numberOfMonths === 1 || numberOfMonths === "1"
                      ? "month"
                      : "months"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Key Information Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Invoice Number */}
        {invoiceId && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block">
                  Invoice No.
                </span>
                <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  #{invoiceId}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Number of Guests */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <UserGroupIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block">
                Guests
              </span>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {numberOfGuests || 0}{" "}
                {numberOfGuests === 1 ? "Guest" : "Guests"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Total Amount - Prominent Display */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-800/10 rounded-xl p-5 border-2 border-green-300 dark:border-green-700 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <CurrencyDollarIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide block">
                Total Amount
              </span>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-0.5">
                {currencySymbol()}
                {Number(totalAmount)?.toFixed(2) || "0.00"}
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-full">
            <svg
              className="w-6 h-6 text-green-600 dark:text-green-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingInfoSummary;
